import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CopyPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { FormItem, FormLabel, FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { cn } from '@/Domain/utils/cn';
import { formatCurrency } from '@/Domain/utils/formatCurrency';

const MAX_NAME_LENGTH = 100;
const MONTHS_PER_YEAR = 12;

// The two ways to buy the plan, each priced on its own card. Derived rather
// than written twice: the pair only differs by which field of the quote it
// reads and which suffix it wears.
// Both secondary, so the two read as peers of one choice rather than a primary
// action beside an alternative -- the same stance the pricing page's own
// interval toggle takes, where the discount pill does the persuading.
const INTERVALS = [
  {
    key: 'monthly',
    priceId: 'price_id_monthly',
    amount: 'price_monthly',
    suffix: 'pricing.per_month',
  },
  {
    key: 'annually',
    priceId: 'price_id_annually',
    amount: 'price_annually',
    suffix: 'pricing.per_year',
  },
] as const;

export interface CloneAgentResponse {
  scenario_id: string;
  name: string;
}

/** A copy too large for the request: the agent appears once the worker finishes. */
export interface QueuedCloneResponse {
  job_id: string;
  name: string;
  total_articles: number;
  total_chunks: number;
}

type CloneResult = CloneAgentResponse | QueuedCloneResponse;

/** Where to pay for the plan the copy needs, and the job that will copy once paid. */
export interface CloneCheckoutStartedResponse {
  session_url: string;
  job_id: string;
}

/** Attached to the 402 when the copy's Knowledge Base outgrows the free tier. */
export interface RequiredPlan {
  required_tier: string;
  articles: number;
  destination_limit: number;
  price_id_monthly: string;
  price_id_annually: string;
  price_monthly: number;
  price_annually: number;
  tier_limit: number | null;
}

const isQueued = (result: CloneResult): result is QueuedCloneResponse => 'job_id' in result;

interface CloneAgentPayload {
  source_scenario_id: string;
  name: string;
  include_knowledge_base: boolean;
  supports_async_clone: true;
}

const cloneAgentSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, i18n.t('sidebar_menu:clone.error.name_required'))
      .max(MAX_NAME_LENGTH, i18n.t('sidebar_menu:clone.error.name_too_long')),
    includeKnowledgeBase: z.boolean(),
  });

type CloneAgentForm = z.infer<ReturnType<typeof cloneAgentSchema>>;

export interface CloneAgentSource {
  scenarioId: string;
  name: string;
}

interface CloneAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: CloneAgentSource;
  container?: HTMLElement | null;
  onQueued?: (queued: QueuedCloneResponse) => void;
  // Kept apart from onQueued: nothing is copying yet, so the caller persists
  // the job without announcing a copy the buyer has not paid for.
  onAwaitingPayment?: (jobId: string) => void;
}

export const CloneAgentDialog = ({
  open,
  onOpenChange,
  source,
  container,
  onQueued,
  onAwaitingPayment,
}: CloneAgentDialogProps) => {
  // subscription_page for the "/month" and "/year" suffixes, so the price
  // fragments are not re-translated into a second vocabulary here.
  const { t } = useTranslation(['sidebar_menu', 'common', 'subscription_page']);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<CloneAgentForm>({
    resolver: zodResolver(cloneAgentSchema()),
    defaultValues: { name: '', includeKnowledgeBase: true },
  });

  useEffect(() => {
    if (!open) return;
    setRequiredPlan(null);
    reset({
      name: t('clone.default_name', { name: source.name }).slice(0, MAX_NAME_LENGTH),
      includeKnowledgeBase: true,
    });
  }, [open, source.name, reset, t]);

  // The hard nav takes seconds to tear the page down; the button must stay busy until it does.
  const [isNavigating, setIsNavigating] = useState(false);
  const [requiredPlan, setRequiredPlan] = useState<RequiredPlan | null>(null);

  const { mutate, isPending } = useMutation<CloneResult, unknown, CloneAgentPayload>({
    mutationKey: [QueryKey.aiAssistants.clone],
    onSuccess: (data) => {
      if (isQueued(data)) {
        // Too large to copy in the request. The agent is hidden until the worker
        // finishes, so there is nowhere to navigate to yet.
        onQueued?.(data);
        onOpenChange(false);
        return;
      }
      setIsNavigating(true);
      // Hard nav, matching how the switcher already changes agent.
      window.location.replace(`/i/${data.scenario_id}`);
    },
    onError: (error: any) => {
      const refusal = error?.response?.data?.errors?.root?.[0];
      if (error?.response?.status === 402 && refusal?.options) {
        // Not a dead end: the refusal carries the plan that would clear it.
        setRequiredPlan(refusal.options as RequiredPlan);
        return;
      }
      // The API serialises every refusal under errors.root; Axios's own message is
      // only ever "Request failed with status code N", so it must not win.
      toast.error(refusal?.message || error?.response?.data?.detail || t('clone.error.failed'));
    },
  });

  const { mutate: startCheckout, isPending: isStartingCheckout } = useMutation<
    CloneCheckoutStartedResponse,
    unknown,
    { source_scenario_id: string; name: string; price_id: string }
  >({
    mutationKey: [QueryKey.aiAssistants.cloneCheckout],
    onSuccess: (data) => {
      setIsNavigating(true);
      // Handed over before we leave: the copy starts on the Stripe webhook, long
      // after this tab is gone, and the caller's watcher is the only thing that
      // reports how it went -- it persists the id for the tab that comes back.
      onAwaitingPayment?.(data.job_id);
      window.location.assign(data.session_url);
    },
    onError: (error: any) => {
      // A collaborator can move the source across a tier boundary while this is
      // open, and the endpoint re-prices before it charges. Dropping the stale
      // quote puts Duplicate back, which re-quotes rather than re-offering a
      // price the server has already refused.
      setRequiredPlan(null);
      setBuyingPriceId(null);
      toast.error(error?.response?.data?.errors?.root?.[0]?.message || t('clone.error.failed'));
    },
  });

  // Computed from the quoted amounts rather than the 17% the pricing page
  // hardcodes, so the badge cannot outlive a change to what Stripe charges.
  const yearlyAtMonthlyRate = (requiredPlan?.price_monthly ?? 0) * MONTHS_PER_YEAR;
  const savedPercent =
    yearlyAtMonthlyRate > 0 && (requiredPlan?.price_annually ?? 0) > 0
      ? Math.round(
          ((yearlyAtMonthlyRate - requiredPlan!.price_annually) / yearlyAtMonthlyRate) * 100,
        )
      : 0;

  const planRef = useRef<HTMLDivElement>(null);
  // The 402 hides the focused submit button; without moving focus a screen
  // reader is left on a control that no longer exists.
  useEffect(() => {
    if (requiredPlan) planRef.current?.focus();
  }, [requiredPlan]);

  const isBusy = isPending || isStartingCheckout || isNavigating;

  // Block dismissal while the copy runs so the user can't leave mid-request and
  // lose the new agent's id.
  const handleOpenChange = (next: boolean) => {
    if (isBusy && !next) return;
    onOpenChange(next);
  };

  // Which interval was clicked, so only that button reports itself busy: the
  // mutation's own isPending is shared, and would blank both names at once.
  const [buyingPriceId, setBuyingPriceId] = useState<string | null>(null);
  const onBuy = (priceId: string) => {
    setBuyingPriceId(priceId);
    startCheckout({
      source_scenario_id: source.scenarioId,
      name: getValues('name'),
      price_id: priceId,
    });
  };

  const onSubmit = (values: CloneAgentForm) =>
    mutate({
      source_scenario_id: source.scenarioId,
      name: values.name,
      include_knowledge_base: values.includeKnowledgeBase,
      supports_async_clone: true,
    });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent container={container} data-testid="clone-agent-dialog">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>
              <CopyPlus className="size-4 bg-muted rounded-lg text-foreground p-1.5 box-content" />
              {t('clone.title')}
            </DialogTitle>
            <DialogDescription>
              {requiredPlan
                ? t('clone.plan.description', {
                    articles: requiredPlan.articles,
                    limit: requiredPlan.destination_limit,
                  })
                : t('clone.description', { name: source.name })}
            </DialogDescription>
          </DialogHeader>

          {requiredPlan && (
            <div
              className="mt-3 mb-1 flex flex-col gap-3"
              data-testid="clone-agent-required-plan"
              // The 402 arrives asynchronously and hides the focused submit
              // button, so without this a screen reader is left on a control
              // that no longer exists and never hears why.
              role="group"
              aria-label={t('clone.plan.aria_label')}
              tabIndex={-1}
              ref={planRef}
            >
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">
                  {t('clone.plan.tier', {
                    tier: SubscriptionHelper.getTrierLabel(requiredPlan.required_tier as TierEnum),
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {requiredPlan.tier_limit
                    ? t('clone.plan.covers', { limit: requiredPlan.tier_limit })
                    : t('clone.plan.covers_unlimited')}
                </p>
              </div>

              {/* Each interval priced on its own card: the choice is which
                  charge to accept, so the amount has to be on the control that
                  accepts it rather than behind it on Stripe. Wraps to one
                  column on narrow dialogs -- "Paiement mensuel" and "Paiement
                  annuel" do not fit side by side. */}
              <div className="grid gap-2 sm:grid-cols-2">
                {INTERVALS.map(({ key, priceId, amount, suffix }) => {
                  const price = requiredPlan[amount];
                  const isBuying = buyingPriceId === requiredPlan[priceId];
                  return (
                    <Button
                      key={key}
                      type="button"
                      variant="secondary"
                      disabled={isBusy}
                      onClick={() => onBuy(requiredPlan[priceId])}
                      data-testid={`clone-agent-plan-${key}`}
                      className="relative h-auto flex-col items-start justify-start gap-1 whitespace-normal px-3.5 py-3 text-left"
                      // The label is hidden to make room for the spinner, so the
                      // name has to come from here or the button becomes anonymous
                      // to a screen reader exactly when it starts doing something.
                      aria-label={
                        price > 0
                          ? t(`clone.plan.${key}_aria`, {
                              amount: formatCurrency(price),
                            })
                          : t(`clone.plan.${key}`)
                      }
                      aria-busy={isBuying}
                    >
                      <span className={cn('flex w-full flex-col gap-1', isBuying && 'invisible')}>
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">{t(`clone.plan.${key}`)}</span>
                          {key === 'annually' && savedPercent > 0 && (
                            // A span, not the shared Badge: that renders a div,
                            // which a button may not contain. Same classes the
                            // pricing page's own interval discount pill uses.
                            <span className="rounded-sm bg-success-muted px-1.5 py-0.5 text-xxs font-medium text-success">
                              {t('clone.plan.percent_off', { percent: savedPercent })}
                            </span>
                          )}
                        </span>
                        {price > 0 && (
                          <span className="flex items-baseline gap-1">
                            <span className="text-lg font-semibold tracking-tight">
                              {formatCurrency(price)}
                            </span>
                            <span className="text-xs font-normal opacity-70">
                              {t(suffix, { ns: 'subscription_page' })}
                            </span>
                          </span>
                        )}
                      </span>
                      {isBuying && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <Spinner className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  );
                })}
              </div>

              {/* When the charge lands and what recurs. A new subscription is
                  not prorated, so the amount due today is the interval price
                  already on the card above -- restating it as a figure would
                  read as a second, different charge. */}
              <div className="flex flex-col gap-1 rounded-lg bg-muted/50 px-3.5 py-3 text-xs text-muted-foreground">
                {requiredPlan.price_monthly > 0 && (
                  <p className="text-foreground">
                    {savedPercent > 0
                      ? t('clone.plan.charged_with_saving', {
                          saving: formatCurrency(yearlyAtMonthlyRate - requiredPlan.price_annually),
                        })
                      : t('clone.plan.charged')}
                  </p>
                )}
                <p>{t('clone.plan.hint')}</p>
              </div>
            </div>
          )}

          <div className={requiredPlan ? 'hidden' : 'space-y-4 py-4'}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="clone-agent-name">{t('clone.field.name')}</FormLabel>
                  <Input
                    id="clone-agent-name"
                    {...field}
                    maxLength={MAX_NAME_LENGTH}
                    disabled={isBusy}
                    data-testid="clone-agent-name-input"
                  />
                  <FormMessage error={errors.name} />
                </FormItem>
              )}
            />

            <Controller
              name="includeKnowledgeBase"
              control={control}
              render={({ field }) => (
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    disabled={isBusy}
                    className="mt-0.5"
                    data-testid="clone-agent-include-kb"
                  />
                  <span className="text-sm text-foreground">
                    {t('clone.field.include_knowledge_base')}
                    <span className="block text-xs text-muted-foreground">
                      {t('clone.field.include_knowledge_base_hint')}
                    </span>
                  </span>
                </label>
              )}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={isBusy}
              onClick={() => handleOpenChange(false)}
            >
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button
              type="submit"
              disabled={isBusy}
              // The plan buttons above are the only way forward once the copy is
              // gated, so offering "Duplicate" here would just re-refuse.
              className={requiredPlan ? 'hidden' : 'relative'}
              data-testid="clone-agent-confirm-button"
            >
              <span className={isBusy ? 'invisible' : ''}>{t('clone.button.confirm')}</span>
              {isBusy && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Spinner className="h-4 w-4" />
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
